import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

interface DeleteAccountDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => Promise<void>
}

export function DeleteAccountDialog({
  isOpen,
  onOpenChange,
  onConfirm
}: DeleteAccountDialogProps) {
  const { t } = useTranslation()
  const [mounted, setMounted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted || !isOpen) return null

  const handleConfirm = async () => {
    try {
      setIsLoading(true)
      await onConfirm()
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-50 bg-black/50 animate-in fade-in-0"
        onClick={() => !isLoading && onOpenChange(false)}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none p-4">
        <div className="bg-background rounded-[var(--card-radius)] shadow-lg max-w-sm w-full animate-in zoom-in-95 pointer-events-auto">
          {/* Content */}
          <div className="p-6 space-y-4 text-center">
            {/* Title */}
            <h2 className="text-xl font-semibold text-foreground">
              {t('settings.deleteAccount', 'Delete Account')}
            </h2>

            {/* Description */}
            <p className="text-base text-foreground leading-relaxed">
              {t('settings.deleteAccountConfirm', 'Are you sure you want to delete your account and all its data?')}
            </p>

            {/* Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => !isLoading && onOpenChange(false)}
                disabled={isLoading}
                className="flex-1 px-4 py-3 rounded-lg border border-border bg-background hover:bg-muted text-foreground font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleConfirm}
                disabled={isLoading}
                className="flex-1 px-4 py-3 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? t('common.deleting', 'Deleting...') : t('common.delete', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
